import React, { useEffect, useState } from 'react';

const VIDEO_EXTENSIONS = /\.(mp4|webm|ogg)(?:\?|$)/i;

export function isMediaVideoUrl(url) {
  return typeof url === 'string' && VIDEO_EXTENSIONS.test(url);
}

function LoadingDots() {
  return (
    <div
      className="absolute inset-0 z-20 flex items-center justify-center bg-secondary-950"
      role="status"
      aria-label="Loading"
    >
      <div className="flex items-center gap-2">
        <span className="h-2 w-2 animate-bounce rounded-full bg-orange-400 [animation-delay:-0.3s] sm:h-2.5 sm:w-2.5" />
        <span className="h-2 w-2 animate-bounce rounded-full bg-orange-400 [animation-delay:-0.15s] sm:h-2.5 sm:w-2.5" />
        <span className="h-2 w-2 animate-bounce rounded-full bg-orange-400 sm:h-2.5 sm:w-2.5" />
      </div>
    </div>
  );
}

/**
 * Fills a fixed frame: blurred cover media behind + sharp contain media on top.
 */
function BlurFillMedia({
  src,
  alt = '',
  className = '',
  mediaClassName = '',
  isVideo = false,
}) {
  const [videoReady, setVideoReady] = useState(!isVideo);

  useEffect(() => {
    setVideoReady(!isVideo);
  }, [src, isVideo]);

  if (!src) return null;

  const markReady = () => setVideoReady(true);

  const commonVideoProps = {
    src,
    autoPlay: true,
    loop: true,
    muted: true,
    playsInline: true,
    preload: 'auto',
    disablePictureInPicture: true,
    onCanPlay: markReady,
    onLoadedData: markReady,
  };

  return (
    <div className={`relative h-full w-full overflow-hidden bg-secondary-950 ${className}`} aria-hidden={!alt}>
      {isVideo && !videoReady ? <LoadingDots /> : null}

      {isVideo ? (
        <video
          {...commonVideoProps}
          className={`pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover object-center blur-xl brightness-75 transition-opacity duration-300 ${
            videoReady ? 'opacity-100' : 'opacity-0'
          }`}
        />
      ) : (
        <img
          src={src}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full scale-110 object-cover object-center blur-xl brightness-90"
        />
      )}
      <div
        className={`absolute inset-0 bg-black/15 transition-opacity duration-300 ${
          videoReady || !isVideo ? 'opacity-100' : 'opacity-0'
        }`}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        {isVideo ? (
          <video
            {...commonVideoProps}
            className={`relative z-10 max-h-full max-w-full object-contain object-center transition-opacity duration-300 [transform:translateZ(0)] ${
              videoReady ? 'opacity-100' : 'opacity-0'
            } ${mediaClassName}`}
          />
        ) : (
          <img
            src={src}
            alt={alt}
            className={`relative z-10 max-h-full max-w-full object-contain object-center ${mediaClassName}`}
          />
        )}
      </div>
    </div>
  );
}

export default BlurFillMedia;
